/**
 * Family refrigerator size calculator — the pure calculation (FRIDGE-1435).
 *
 * Moved out of app/tools/family-refrigerator-size-calculator/page-client.tsx,
 * unchanged in behavior, so the page's FAQ can quote the calculator's own
 * result instead of a typed range. Children count as 0.7 of an adult; 5 cu ft
 * per person; cooking and shopping multipliers; the range is ±15% of the
 * recommendation.
 */
import { readCount } from './numeric-input'

export interface FamilyRefrigeratorSizeInput {
  adults: string
  children: string
  cookingFrequency: string
  shoppingFrequency: string
}

export interface FamilyRefrigeratorSizeResult {
  minCapacity: number
  recommendedCapacity: number
  maxCapacity: number
  fridgeType: string
  freezerSize: string
}

/** The inputs the page opens with: two adults, two children, daily cooking, weekly shopping. */
export const FAMILY_REFRIGERATOR_SIZE_DEFAULTS: Readonly<FamilyRefrigeratorSizeInput> = {
  adults: '2',
  children: '2',
  cookingFrequency: 'daily',
  shoppingFrequency: 'weekly',
}

export function calculateFamilyRefrigeratorSize(input: FamilyRefrigeratorSizeInput): FamilyRefrigeratorSizeResult | null {
  const { adults, children, cookingFrequency, shoppingFrequency } = input
  const numAdults = readCount(adults, { min: 1, max: 20 }) ?? 0
  const numChildren = readCount(children, { min: 1, max: 20 }) ?? 0
  if (numAdults + numChildren === 0) return null

  const totalPeople = numAdults + (numChildren * 0.7) // Children weighted as 0.7 of adult
  const baseCapacity = totalPeople * 5

  const cookingMultiplier =
    cookingFrequency === "rarely" ? 0.8 :
    cookingFrequency === "sometimes" ? 1.0 :
    cookingFrequency === "daily" ? 1.2 :
    cookingFrequency === "multiple" ? 1.4 : 1.0

  const shoppingMultiplier =
    shoppingFrequency === "daily" ? 0.8 :
    shoppingFrequency === "weekly" ? 1.0 :
    shoppingFrequency === "biweekly" ? 1.3 :
    shoppingFrequency === "monthly" ? 1.6 : 1.0

  const adjustedCapacity = baseCapacity * cookingMultiplier * shoppingMultiplier
  const minCapacity = Math.round(adjustedCapacity * 0.85)
  const recommendedCapacity = Math.round(adjustedCapacity)
  const maxCapacity = Math.round(adjustedCapacity * 1.15)

  let fridgeType = ""
  let freezerSize = ""
  if (recommendedCapacity < 14) {
    fridgeType = "Compact or top-freezer (10–14 cu ft)"
    freezerSize = "3–5 cu ft"
  } else if (recommendedCapacity < 20) {
    fridgeType = "Standard top-freezer or bottom-freezer (14–20 cu ft)"
    freezerSize = "5–7 cu ft"
  } else if (recommendedCapacity < 26) {
    fridgeType = "French door or side-by-side (20–26 cu ft)"
    freezerSize = "7–9 cu ft"
  } else {
    fridgeType = "Large French door or side-by-side (26+ cu ft)"
    freezerSize = "9–12 cu ft"
  }

  return { minCapacity, recommendedCapacity, maxCapacity, fridgeType, freezerSize }
}
